import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Bookmark,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  RotateCcw,
  Sparkles,
  Star,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/ui/dialog";
import { usePublicGalleryVersionURLs, type PublicGalleryScope } from "@/features/gallery/api";
import type { PublicGalleryAsset } from "@/features/gallery/types";
import { CustomerComments } from "./CustomerComments";
import { ROUND, type SelectionStage } from "./CustomerGalleryControls";

// Màn xem MỘT ảnh — chỉnh theo dự án PHOTO (CEO chốt 27/08/2026): nền xám trung tính thay nền
// đen (ảnh nổi hơn, đúng token --view-bg của PHOTO), vuốt trái/phải trên điện thoại để chuyển
// ảnh (ngưỡng 60px — đủ xa để không nhầm với cú chạm run tay), và nút chọn đi theo BƯỚC đang
// chọn (Nháp/Cuối) thay vì hai nút cạnh nhau.

const VERSION_LABEL = { original: "Ảnh gốc", edit: "Bản chỉnh sửa", final: "Bản hoàn thiện" } as const;
const FILMSTRIP_SIZE = 9;
/** Nền màn xem ảnh — token `--view-bg` của PHOTO. */
const VIEW_BG = "#7D7D7D";

type CustomerViewerProps = {
  scope: PublicGalleryScope;
  assets: PublicGalleryAsset[];
  asset: PublicGalleryAsset | null;
  canComment: boolean;
  canDownload: boolean;
  /**
   * Vì sao nút tải đang khoá, nguyên văn lời máy chủ. Rỗng = không khoá vì tiền.
   *
   * Khoá vì tiền KHÁC với không có quyền tải: khách trả đủ là mở, nên nút vẫn
   * phải HIỆN kèm lý do (CEO chốt 1a 26/09/2026) — giấu nút đi thì khách nhắn
   * studio hỏi "sao không tải được", và ai nhận tin cũng không biết trả lời.
   */
  downloadLockReason?: string;
  canSelect: boolean;
  locked: boolean;
  stage: SelectionStage;
  draftSelectedIds: Set<string>;
  finalSelectedIds: Set<string>;
  onAssetChange: (asset: PublicGalleryAsset) => void;
  onToggleDraft: (asset: PublicGalleryAsset, selected: boolean) => void;
  onToggleFinal: (asset: PublicGalleryAsset, selected: boolean) => void;
  onClose: () => void;
};

export function CustomerViewer(props: CustomerViewerProps) {
  const { asset, onClose } = props;
  return (
    <Dialog open={asset !== null} onOpenChange={(open) => !open && onClose()}>
      {asset ? (
        <ViewerContent key={`${asset.assetId}:${asset.version.id}`} {...props} asset={asset} />
      ) : null}
    </Dialog>
  );
}

function ViewerContent({
  scope,
  assets,
  asset,
  canComment,
  canDownload,
  downloadLockReason = "",
  canSelect,
  locked,
  stage,
  draftSelectedIds,
  finalSelectedIds,
  onAssetChange,
  onToggleDraft,
  onToggleFinal,
  onClose,
}: CustomerViewerProps & { asset: PublicGalleryAsset }) {
  const cachedURLs = asset.urls;
  const currentIndex = Math.max(
    0,
    assets.findIndex((item) => item.assetId === asset.assetId),
  );
  const previousAsset = assets[currentIndex - 1];
  const nextAsset = assets[currentIndex + 1];
  const draftSelected = draftSelectedIds.has(asset.assetId);
  const finalSelected = finalSelectedIds.has(asset.assetId);
  const [zoom, setZoom] = useState(1);
  const touchX = useRef<number | null>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest("input, textarea, select, [contenteditable='true']")
      )
        return;
      if (event.key === "ArrowLeft" && previousAsset) {
        event.preventDefault();
        onAssetChange(previousAsset);
      }
      if (event.key === "ArrowRight" && nextAsset) {
        event.preventDefault();
        onAssetChange(nextAsset);
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [nextAsset, onAssetChange, onClose, previousAsset]);

  return (
    <DialogContent
      hideCloseButton
      className="h-[100dvh] w-screen max-w-none gap-0 overflow-y-auto rounded-none border-0 p-0 sm:max-w-none md:inset-0 md:left-0 md:top-0 md:max-h-[100dvh] md:max-w-none md:translate-x-0 md:translate-y-0 md:overflow-hidden md:rounded-none md:border-0 md:p-0"
    >
      <div className="grid min-h-full md:h-full md:min-h-0 md:grid-cols-[minmax(0,1fr)_minmax(300px,34vw)] xl:grid-cols-[minmax(0,1fr)_380px]">
        <section
          className="grid min-h-[55dvh] min-w-0 grid-rows-[minmax(0,1fr)_88px] md:min-h-0 md:grid-rows-[minmax(0,1fr)_104px]"
          style={{ background: VIEW_BG }}
        >
          <div
            className="relative min-h-0 overflow-hidden"
            onTouchStart={(event) => {
              touchX.current = event.touches[0]?.clientX ?? null;
            }}
            onTouchEnd={(event) => {
              const start = touchX.current;
              touchX.current = null;
              const end = event.changedTouches[0]?.clientX;
              if (start === null || end === undefined) return;
              if (end - start > 60 && previousAsset) onAssetChange(previousAsset);
              else if (start - end > 60 && nextAsset) onAssetChange(nextAsset);
            }}
          >
            <div className="absolute inset-3 grid place-items-center sm:inset-5">
              {cachedURLs?.large ? (
                <img
                  src={cachedURLs.large}
                  alt={asset.originalFilename}
                  className="h-auto max-h-full min-h-0 w-auto max-w-full min-w-0 object-contain transition-transform duration-150"
                  style={{ transform: `scale(${zoom})` }}
                />
              ) : (
                <RemoteViewerImage scope={scope} asset={asset} zoom={zoom} />
              )}
            </div>
            <div className="absolute left-3 top-3 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white">
              {currentIndex + 1} / {assets.length}
            </div>
            <ZoomControls zoom={zoom} onZoomChange={setZoom} />
            <ViewerArrow
              label="Ảnh trước"
              side="left"
              disabled={!previousAsset}
              onClick={() => previousAsset && onAssetChange(previousAsset)}
            >
              <ChevronLeft />
            </ViewerArrow>
            <ViewerArrow
              label="Ảnh tiếp theo"
              side="right"
              disabled={!nextAsset}
              onClick={() => nextAsset && onAssetChange(nextAsset)}
            >
              <ChevronRight />
            </ViewerArrow>
          </div>
          <Filmstrip
            scope={scope}
            assets={assets}
            currentIndex={currentIndex}
            onAssetChange={onAssetChange}
          />
        </section>

        <aside className="min-h-0 border-t bg-background md:overflow-y-auto md:border-l md:border-t-0">
          <div className="space-y-5 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <DialogTitle className="break-words text-base leading-snug">
                  {asset.originalFilename}
                </DialogTitle>
                <DialogDescription className="mt-1">
                  Phiên bản {asset.version.versionNumber} · {VERSION_LABEL[asset.version.kind]}
                </DialogDescription>
              </div>
              <Button
                type="button"
                size="icon"
                variant="outline"
                aria-label="Đóng ảnh"
                onClick={onClose}
              >
                <X />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2 border-y py-3 text-sm">
              <StatusItem
                active={asset.recommended}
                activeLabel="Photo đề xuất"
                icon={<Star />}
                label="Đề xuất"
              />
              <StatusItem
                active={asset.version.kind === "final"}
                icon={<Sparkles />}
                label="Bản hoàn thiện"
              />
              <StatusItem
                active={draftSelected}
                icon={<Bookmark />}
                label={`Trong ${ROUND.draft.short}`}
              />
              <StatusItem
                active={finalSelected}
                icon={<CheckCircle2 />}
                label={`Trong ${ROUND.final.short}`}
              />
            </div>

            {canSelect && !locked ? (
              // Một nút theo VÒNG đang chọn (dải Vòng 1 → Vòng 2 của trang) — đúng lối PHOTO:
              // ngữ cảnh quyết định trái tim ghi vào đâu, mỗi màn chỉ một hành động chính.
              <Button
                type="button"
                className="w-full"
                variant={(stage === "final" ? finalSelected : draftSelected) ? "default" : "outline"}
                aria-label={`${
                  (stage === "final" ? finalSelected : draftSelected) ? "Bỏ chọn" : "Chọn"
                } ${ROUND[stage].short} ${asset.originalFilename}`}
                aria-pressed={stage === "final" ? finalSelected : draftSelected}
                onClick={() =>
                  stage === "final"
                    ? onToggleFinal(asset, !finalSelected)
                    : onToggleDraft(asset, !draftSelected)
                }
              >
                {stage === "final" ? (
                  <>
                    <CheckCircle2 className={finalSelected ? "fill-current" : ""} />
                    {finalSelected ? `Bỏ khỏi ${ROUND.final.short}` : `Chọn vào ${ROUND.final.short}`}
                  </>
                ) : (
                  <>
                    <Bookmark className={draftSelected ? "fill-current" : ""} />
                    {draftSelected ? `Bỏ khỏi ${ROUND.draft.short}` : `Chọn vào ${ROUND.draft.short}`}
                  </>
                )}
              </Button>
            ) : null}

            {canDownload && cachedURLs?.download ? (
              <DownloadButton url={cachedURLs.download} />
            ) : canDownload ? (
              <RemoteDownloadButton scope={scope} asset={asset} />
            ) : downloadLockReason ? (
              <LockedDownloadButton reason={downloadLockReason} />
            ) : null}

            <CustomerComments scope={scope} asset={asset} canComment={canComment} />
          </div>
        </aside>
      </div>
    </DialogContent>
  );
}

function RemoteViewerImage({
  scope,
  asset,
  zoom,
}: {
  scope: PublicGalleryScope;
  asset: PublicGalleryAsset;
  zoom: number;
}) {
  const urls = usePublicGalleryVersionURLs(
    scope,
    asset.assetId,
    asset.version.id,
    asset.version.status === "ready",
  );
  return urls.data?.large ? (
    <img
      src={urls.data.large}
      alt={asset.originalFilename}
      className="h-auto max-h-full min-h-0 w-auto max-w-full min-w-0 object-contain transition-transform duration-150"
      style={{ transform: `scale(${zoom})` }}
    />
  ) : (
    <p className="text-sm text-white/70">Đang tải ảnh</p>
  );
}

function DownloadButton({ url }: { url: string }) {
  return (
    <Button type="button" variant="outline" className="w-full" asChild>
      <a href={url}>
        <Download /> Tải ảnh này
      </a>
    </Button>
  );
}

/**
 * Nút tải đang khoá vì tiền: vẫn thấy, bấm không được, và NÓI RÕ vì sao.
 *
 * `title` cho chuột, `aria-label` cho trình đọc màn hình, và câu chữ ngay dưới
 * nút cho người chỉ nhìn — nút khoá mà không có dòng chữ đó thì khách trên điện
 * thoại (không rê chuột được) chỉ thấy một nút chết.
 */
function LockedDownloadButton({ reason }: { reason: string }) {
  return (
    <div className="w-full space-y-1">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled
        title={reason}
        aria-label={`Tải ảnh này — ${reason}`}
      >
        <Download /> Tải ảnh này
      </Button>
      <p className="text-xs text-white/80">{reason}</p>
    </div>
  );
}

function RemoteDownloadButton({
  scope,
  asset,
}: {
  scope: PublicGalleryScope;
  asset: PublicGalleryAsset;
}) {
  const urls = usePublicGalleryVersionURLs(
    scope,
    asset.assetId,
    asset.version.id,
    asset.version.status === "ready",
  );
  return urls.data?.download ? <DownloadButton url={urls.data.download} /> : null;
}

function ZoomControls({
  zoom,
  onZoomChange,
}: {
  zoom: number;
  onZoomChange: (zoom: number) => void;
}) {
  return (
    <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-md bg-black/75 p-1 text-white shadow">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label="Thu nhỏ"
        disabled={zoom <= 0.5}
        className="text-white hover:bg-white/15 hover:text-white"
        onClick={() => onZoomChange(Math.max(0.5, zoom - 0.25))}
      >
        <ZoomOut />
      </Button>
      <span className="w-12 text-center text-xs tabular-nums">{Math.round(zoom * 100)}%</span>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label="Phóng to"
        disabled={zoom >= 3}
        className="text-white hover:bg-white/15 hover:text-white"
        onClick={() => onZoomChange(Math.min(3, zoom + 0.25))}
      >
        <ZoomIn />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label="Đặt lại thu phóng"
        disabled={zoom === 1}
        className="text-white hover:bg-white/15 hover:text-white"
        onClick={() => onZoomChange(1)}
      >
        <RotateCcw />
      </Button>
    </div>
  );
}

function StatusItem({
  active,
  activeLabel,
  icon,
  label,
}: {
  active: boolean;
  activeLabel?: string;
  icon: ReactNode;
  label: string;
}) {
  return (
    <div
      aria-label={active ? activeLabel : undefined}
      className={`flex items-center gap-2 ${active ? "font-semibold text-foreground" : "text-muted-foreground"}`}
    >
      <span className={`[&_svg]:h-4 [&_svg]:w-4 ${active ? "[&_svg]:fill-amber-400" : ""}`}>
        {icon}
      </span>
      {label}
    </div>
  );
}

function ViewerArrow({
  label,
  side,
  disabled,
  onClick,
  children,
}: {
  label: string;
  side: "left" | "right";
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant="secondary"
      aria-label={label}
      disabled={disabled}
      className={`absolute top-1/2 -translate-y-1/2 bg-white/90 shadow ${side === "left" ? "left-3" : "right-3"}`}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function Filmstrip({
  scope,
  assets,
  currentIndex,
  onAssetChange,
}: {
  scope: PublicGalleryScope;
  assets: PublicGalleryAsset[];
  currentIndex: number;
  onAssetChange: (asset: PublicGalleryAsset) => void;
}) {
  const halfWindow = Math.floor(FILMSTRIP_SIZE / 2);
  const start = Math.max(
    0,
    Math.min(currentIndex - halfWindow, Math.max(assets.length - FILMSTRIP_SIZE, 0)),
  );
  const visibleAssets = assets.slice(start, start + FILMSTRIP_SIZE);
  return (
    <nav
      aria-label="Danh sách ảnh"
      className="flex min-w-0 items-center gap-2 overflow-x-auto border-t border-white/15 bg-neutral-950 px-3 py-2"
    >
      {visibleAssets.map((item) =>
        item.urls?.thumb ? (
          <FilmstripItem
            key={item.assetId}
            item={item}
            url={item.urls.thumb}
            active={item.assetId === assets[currentIndex]?.assetId}
            onClick={() => onAssetChange(item)}
          />
        ) : (
          <RemoteFilmstripItem
            key={item.assetId}
            scope={scope}
            item={item}
            active={item.assetId === assets[currentIndex]?.assetId}
            onClick={() => onAssetChange(item)}
          />
        ),
      )}
    </nav>
  );
}

function FilmstripItem({
  item,
  url,
  active,
  onClick,
}: {
  item: PublicGalleryAsset;
  url: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Mở ảnh ${item.originalFilename}`}
      aria-current={active ? "true" : undefined}
      className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-sm border-2 bg-neutral-800 md:h-20 md:w-20 ${active ? "border-white" : "border-transparent opacity-65 hover:opacity-100"}`}
      onClick={onClick}
    >
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : null}
      {item.recommended ? (
        <Star className="absolute right-1 top-1 h-3.5 w-3.5 fill-amber-400 text-amber-600" />
      ) : null}
    </button>
  );
}

function RemoteFilmstripItem({
  scope,
  item,
  active,
  onClick,
}: {
  scope: PublicGalleryScope;
  item: PublicGalleryAsset;
  active: boolean;
  onClick: () => void;
}) {
  const urls = usePublicGalleryVersionURLs(
    scope,
    item.assetId,
    item.version.id,
    item.version.status === "ready",
  );
  return (
    <FilmstripItem
      item={item}
      url={urls.data?.thumb ?? ""}
      active={active}
      onClick={onClick}
    />
  );
}
